import React, { useState, useEffect } from "react";
import Select from "react-select";
import axiosInstance from "../utils/AxiosInstance";
import CreatableSelect from "react-select/creatable";

const INDICATOR_TYPES = [
  { value: "email", label: "Email" },
  { value: "hash", label: "Hash" },
  { value: "url", label: "URL" },
  { value: "domain", label: "Domain" },
  { value: "ip", label: "IP" },
];

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "false_positive", label: "False Positive" },
  { value: "expired", label: "Expired" },
];

const EXPIRY_OPTIONS = [
  { value: "never", label: "Never" },
  { value: "30", label: "30 Days" },
  { value: "90", label: "90 Days" },
  { value: "365", label: "1 Year" },
];

function validateIndicator(indicator, type) {
  const hashRegex = /^[a-fA-F0-9]{32}$|^[a-fA-F0-9]{40}$|^[a-fA-F0-9]{64}$/;
  const ipRegex = /^(?:\d{1,3}\.){3}\d{1,3}$|^(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/;
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const domainRegex = /^(?=.{1,253}$)(?:[a-zA-Z0-9_](?:[a-zA-Z0-9_-]{0,61}[a-zA-Z0-9_])?\.)+(?:[a-zA-Z]{2,}|xn--[a-zA-Z0-9]{2,})$/;
  const urlRegex = /^(https?:\/\/)([a-zA-Z0-9.-]+|\d{1,3}(?:\.\d{1,3}){3})(:\d+)?(\/[^\s]*)?$/i;

  switch (type) {
    case "hash":
      return hashRegex.test(indicator);
    case "ip":
      return ipRegex.test(indicator);
    case "email":
      return emailRegex.test(indicator);
    case "domain":
      return domainRegex.test(indicator);
    case "url":
      return urlRegex.test(indicator);
    default:
      return false;
  }
}

const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    backgroundColor: "#1f2937",
    color: "#e5e7eb",
    borderColor: "#4b5563",
    boxShadow: state.isFocused ? "0 0 0 2px #a855f7" : "none",
    "&:hover": {
      borderColor: "#a855f7",
    },
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: "#1f2937",
    color: "#e5e7eb",
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#6b21a8",
    color: "#fff",
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "#fff",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? "#6b21a8" : "#1f2937",
    color: "#e5e7eb",
    cursor: "pointer",
  }),
  input: (base) => ({
    ...base,
    color: "#e5e7eb",
  }),
  singleValue: (base) => ({
    ...base,
    color: "#e5e7eb",
  }),
};

export default function ShareIndicator() {
  const [form, setForm] = useState({
    indicator: "",
    indicator_type: INDICATOR_TYPES[4],
    confidence: 0,
    risk_score: 0,
    description: "",
    justification: "",
    status: STATUS_OPTIONS[0],
    expiry: EXPIRY_OPTIONS[0],
    tags: [],
  });

  const [tagCategories, setTagCategories] = useState([]);
  const [loadingTags, setLoadingTags] = useState(true);
  const [error, setError] = useState(null);
  const [submitMessage, setSubmitMessage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    async function fetchTags() {
      try {
        const { data } = await axiosInstance.get("share/tag-categories/");
        setTagCategories(data.categories);
      } catch (err) {
        setError("Failed to load tags.");
      } finally {
        setLoadingTags(false);
      }
    }
    fetchTags();
  }, []);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    const val = type === "number" ? Number(value) : value;
    if (error) setError(null);
    if (submitMessage) setSubmitMessage(null);
    setForm((prev) => ({ ...prev, [name]: val }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitMessage(null);

    const trimmedIndicator = form.indicator.trim();
    if (!trimmedIndicator) return setError("Indicator is required.");

    if (!validateIndicator(trimmedIndicator, form.indicator_type.value)) {
      return setError(`Invalid ${form.indicator_type.label} format.`);
    }

    if (form.confidence < 0 || form.confidence > 100)
      return setError("Confidence must be between 0 and 100.");
    if (form.risk_score < 0 || form.risk_score > 100)
      return setError("Risk Score must be between 0 and 100.");

    let expiry_date = null;
    if (form.expiry.value !== "never") {
      const daysToAdd = parseInt(form.expiry.value, 10);
      const now = new Date();
      now.setDate(now.getDate() + daysToAdd);
      expiry_date = now.toISOString().split("T")[0];
    }

    setIsSubmitting(true);

    try {
      const selectedTags = form.tags.map((tag) =>
        typeof tag === "string" ? tag : tag.value ?? tag.label
      );

      await axiosInstance.post("share/submit/", {
        ...form,
        indicator: trimmedIndicator,
        indicator_type: form.indicator_type.value,
        status: form.status.value,
        tags: selectedTags,
        source: "user",
        expiry_date,
      });

      setSubmitMessage("Indicator submitted successfully!");
      setForm({
        indicator: "",
        indicator_type: INDICATOR_TYPES[4],
        confidence: 0,
        risk_score: 0,
        description: "",
        justification: "",
        status: STATUS_OPTIONS[0],
        expiry: EXPIRY_OPTIONS[0],
        tags: [],
      });
        } catch (err) {
      let errorMessage =
        err.response?.data?.errors?.indicator?.[0] ||
        err.response?.data?.detail ||
        "Failed to submit indicator. Please try again.";

      // Optional: clean up specific backend wording
      if (errorMessage === "indicator with this indicator already exists.") {
        errorMessage = "An indicator with this value already exists.";
      }

      setError(errorMessage);
    }finally {
      setIsSubmitting(false);
    }
  };


  return (
    <div className="max-w-3xl mx-auto p-4 bg-gray-900 text-gray-200 rounded-md">
      <h1 className="text-3xl font-semibold mb-6">Share a New Indicator</h1>

      {error && <div className="bg-red-700 text-red-100 p-3 rounded mb-4">{error}</div>}
      {submitMessage && <div className="bg-green-700 text-green-100 p-3 rounded mb-4">{submitMessage}</div>}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="indicator" className="block font-semibold mb-1">
            Indicator <span className="text-red-500">*</span>
          </label>
          <input
            id="indicator"
            name="indicator"
            type="text"
            value={form.indicator}
            onChange={handleChange}
            required
            className="w-full rounded border border-gray-600 p-2 bg-gray-800 text-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder="e.g., 8.8.8.8 or bad-domain.com"
            disabled={isSubmitting}
          />
        </div>

        <div>
          <label className="block font-semibold mb-1">
            Indicator Type <span className="text-red-500">*</span>
          </label>
          <Select
            value={form.indicator_type}
            onChange={(selected) => setForm((f) => ({ ...f, indicator_type: selected }))}
            options={INDICATOR_TYPES}
            styles={customSelectStyles}
            isDisabled={isSubmitting}
          />
        </div>

        <div>
          <label className="block font-semibold mb-1">Confidence (%)</label>
          <input
            name="confidence"
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={form.confidence}
            onChange={handleChange}
            className="w-full rounded border border-gray-600 p-2 bg-gray-800 text-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder="e.g., 75.5"
            disabled={isSubmitting}
          />
        </div>

        <div>
          <label className="block font-semibold mb-1">Risk Score</label>
          <input
            name="risk_score"
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={form.risk_score}
            onChange={handleChange}
            className="w-full rounded border border-gray-600 p-2 bg-gray-800 text-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder="e.g., 50"
            disabled={isSubmitting}
          />
        </div>

        <div>
          <label className="block font-semibold mb-1">Description / Justification</label>
          <textarea
            name="description"
            value={form.description}
            onChange={handleChange}
            rows={3}
            className="w-full rounded border border-gray-600 p-2 bg-gray-800 text-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder="Optional description or justification"
            disabled={isSubmitting}
          />
        </div>

        <div>
          <label className="block font-semibold mb-1">Additional Justification</label>
          <textarea
            name="justification"
            value={form.justification}
            onChange={handleChange}
            rows={2}
            className="w-full rounded border border-gray-600 p-2 bg-gray-800 text-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder="Optional notes"
            disabled={isSubmitting}
          />
        </div>

        <div>
          <label className="block font-semibold mb-1">Status</label>
          <Select
            value={form.status}
            onChange={(selected) => setForm((f) => ({ ...f, status: selected }))}
            options={STATUS_OPTIONS}
            styles={customSelectStyles}
            isDisabled={isSubmitting}
          />
        </div>

        <div>
          <label className="block font-semibold mb-1">IOC Expiry</label>
          <Select
            value={form.expiry}
            onChange={(selected) => setForm((f) => ({ ...f, expiry: selected }))}
            options={EXPIRY_OPTIONS}
            styles={customSelectStyles}
            isDisabled={isSubmitting}
          />
          <p className="text-sm text-gray-400 mt-1">
            Select how long this indicator should be considered active. Expired indicators may be flagged or deprioritized.
          </p>
        </div>

        <div>
          <label className="block font-semibold mb-2">
            Tags
            <span
              className="ml-2 text-sm text-gray-400 cursor-help"
              title="Enter one or more tags. You may type your own or select from suggestions like: `malware`, `c2`, `phishing`, `ransomware`, etc."
            >
              (?)
            </span>
          </label>

          <CreatableSelect
            isMulti
            styles={customSelectStyles}
            placeholder="Enter tags (e.g., phishing, c2, ransomware)"
            value={form.tags}
            onChange={(selected) => setForm((prev) => ({ ...prev, tags: selected }))}
            options={
              tagCategories.flatMap((cat) =>
                cat.tags.map((tag) => ({
                  label: tag.name,
                  value: tag.slug,
                }))
              )
            }
            isDisabled={isSubmitting}
          />
        </div>

        <button
          type="submit"
          disabled={loadingTags || isSubmitting}
          className={`bg-purple-600 hover:bg-purple-700 transition-colors text-white font-semibold px-6 py-3 rounded ${
            loadingTags || isSubmitting ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          {isSubmitting ? "Submitting..." : "Submit Indicator"}
        </button>
      </form>
    </div>
  );
}
